import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-pill px-3 py-1 text-body-xs font-semibold w-fit",
  {
    variants: {
      variant: {
        // Solid (matches Figma Make news badges) — bg cor de categoria + texto branco
        governo: "bg-bg-dark text-on-primary",
        saude: "bg-cat-saude text-on-primary",
        educacao: "bg-cat-educacao text-on-primary",
        trabalho: "bg-cat-trabalho text-on-accent",
        moradia: "bg-cat-moradia text-on-primary",
        aposentadoria: "bg-cat-aposentadoria text-on-primary",
        assistencia: "bg-cat-assistencia-social text-on-primary",
        mobilidade: "bg-cat-mobilidade text-on-primary",
        // Soft / neutral
        destaque: "bg-badge-destaque-bg text-badge-destaque-fg",
        neutral: "bg-bg-secondary text-fg-strong",
        // Feedback / status — usados nos badges do Painel do Cidadão
        danger: "bg-feedback-danger-bg text-feedback-danger",
        warning: "bg-feedback-warning-bg text-feedback-warning",
        caution: "bg-feedback-caution-bg text-feedback-caution",
        success: "bg-feedback-success-bg text-feedback-success",
        info: "bg-feedback-info-bg text-feedback-info",
      },
      /**
       * `outline` deixa o badge mais leve: fundo branco, texto azul escuro e a
       * cor da categoria só no contorno. A paleta por categoria é exatamente a
       * mesma do sólido — muda só onde a cor é aplicada.
       *
       * Em teste no "Recomendado para você" do painel antes de virar padrão.
       */
      tone: {
        solid: "",
        outline: "border bg-surface text-fg-strong",
      },
    },
    compoundVariants: [
      // No outline o `variant` deixa de pintar o fundo e passa a pintar a borda.
      { tone: "outline", variant: "governo", class: "border-bg-dark" },
      { tone: "outline", variant: "saude", class: "border-cat-saude" },
      { tone: "outline", variant: "educacao", class: "border-cat-educacao" },
      { tone: "outline", variant: "trabalho", class: "border-cat-trabalho" },
      { tone: "outline", variant: "moradia", class: "border-cat-moradia" },
      { tone: "outline", variant: "aposentadoria", class: "border-cat-aposentadoria" },
      { tone: "outline", variant: "assistencia", class: "border-cat-assistencia-social" },
      { tone: "outline", variant: "mobilidade", class: "border-cat-mobilidade" },
      // No destaque o contorno usa a cor de FUNDO do sólido: o `fg` dele é
      // branco e sumiria contra a superfície.
      { tone: "outline", variant: "destaque", class: "border-badge-destaque-bg" },
      // `border-default` (#d6dbe6) sumia como contorno de 1px — no sólido o
      // neutro ainda era um chip preenchido. `fg-subtle` mantém o chip visível
      // sem competir com as categorias coloridas.
      { tone: "outline", variant: "neutral", class: "border-fg-subtle" },
      { tone: "outline", variant: "danger", class: "border-feedback-danger" },
      { tone: "outline", variant: "warning", class: "border-feedback-warning" },
      { tone: "outline", variant: "caution", class: "border-feedback-caution" },
      { tone: "outline", variant: "success", class: "border-feedback-success" },
      { tone: "outline", variant: "info", class: "border-feedback-info" },
    ],
    defaultVariants: { variant: "neutral", tone: "solid" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, tone, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant, tone, className }))} {...props} />
  ),
);
Badge.displayName = "Badge";

export { badgeVariants };

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;
export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>["tone"]>;
